import styled from 'styled-components';

// T2-6049: Talent Invoices list now mirrors the DO Invoices page styling
// (T2-6048) — same header, sort pills, card layout, status pill + amount
// right-aligned, sent/shift meta row with icons.

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f8f9fa;
  font-family: var(--fontMain, Poppins), sans-serif;
`;

export const Content = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 16px;
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

export const Header = styled.div`
  margin-bottom: 20px;
`;

export const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary, #222);
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

export const Subtitle = styled.p`
  font-size: 14px;
  color: var(--text-secondary, #666);
  margin: 0;
`;

export const SortBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  background: #fff;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid var(--divider, #E0E0E0);
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const SortButton = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid ${p => (p.active ? 'var(--primary-blue, #0B4598)' : 'transparent')};
  background: ${p => (p.active ? 'rgba(11,69,152,0.08)' : 'transparent')};
  color: ${p => (p.active ? 'var(--primary-blue, #0B4598)' : 'var(--text-primary, #333)')};
  font-weight: ${p => (p.active ? '600' : '500')};
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${p => (p.active ? 'rgba(11,69,152,0.12)' : 'rgba(11,69,152,0.04)')};
  }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
`;

// T2-6049: Mirrors the DO invoices card layout (T2-6048). Office name on
// the LEFT with the Sent/Shift dates row tucked tightly underneath; status
// pill + amount stacked on the RIGHT.
export const ListItem = styled.button`
  text-align: left;
  width: 100%;
  background: #fff;
  border: 1px solid var(--divider, #E0E0E0);
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  font-family: inherit;

  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    border-color: var(--primary-blue, #0B4598);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

export const ItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const ItemTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: var(--text-primary, #222);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

export const StatusBadge = styled.span<{ paid?: boolean; sent?: boolean; processing?: boolean }>`
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: ${p =>
    p.paid
      ? 'rgba(19, 202, 130, 0.16)'
      : p.processing
        ? 'rgba(11, 69, 152, 0.14)'
      : p.sent
        ? 'rgba(251, 118, 114, 0.16)'
        : 'rgba(0, 0, 0, 0.08)'};
  color: ${p => (p.paid ? '#0E9F65' : p.processing ? 'var(--primary-blue, #0B4598)' : p.sent ? '#D9534F' : '#666')};
  white-space: nowrap;
  text-transform: uppercase;
`;

export const ItemAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-blue, #0B4598);
  line-height: 1.1;
`;

export const ItemMeta = styled.div`
  display: flex;
  gap: 18px;
  color: var(--text-secondary, #666);
  font-size: 13px;
  flex-wrap: wrap;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  svg {
    font-size: 16px;
    color: var(--text-secondary, #888);
  }
`;

export const Empty = styled.div`
  text-align: center;
  margin-top: 80px;

  .icon {
    font-size: 64px;
    margin-bottom: 16px;
  }

  .title {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary, #222);
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 14px;
    color: var(--text-secondary, #666);
  }
`;
